'use client';

import { readLocalCartInfo, useCartInfoWriter } from '@/state';
import {
  clearCartMergePending,
  hasCartMergePending,
  mergeCartWithNotification,
} from '@/utils/mergeCartWithNotification';
import * as Sentry from '@sentry/nextjs';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';

/**
 * ローカルカートのバージョン整合性チェック + マージ失敗時のセーフティネット。
 *
 * - `NEXT_PUBLIC_LOCAL_CART_VERSION` が変わったら localStorage をクリアする
 * - `cartMergePending` フラグが立っている = `useSignInWithCartMerge` でのマージが恒久失敗
 *   している場合に、ログイン中 & ローカルカートあり の条件で再試行する。
 *
 * 再試行はページ遷移ごとに1回だけ叩く。ログイン時（signInAndMerge）は指数バックオフで粘るが、
 * こちらは次の遷移でまた機会が来るセーフティネットなのでリトライしない。
 */
function GfCheckCart({ isLogin }: { isLogin: boolean }) {
  const { clearCartInfo } = useCartInfoWriter();
  const router = useRouter();
  // レイアウトはクライアント遷移で再マウントされないため、再試行の起点として遷移ごとに変わる値が必要
  const pathname = usePathname();
  const retryingRef = useRef(false);

  useEffect(() => {
    // 30日経過したアイテムの削除はAPI側（hasExpiredItems）に委譲。ここはバージョン不一致の破棄のみ
    const { cartVersion } = readLocalCartInfo();
    const isOldVersion = !cartVersion || cartVersion !== Number(process.env.NEXT_PUBLIC_LOCAL_CART_VERSION || 1);
    if (!isOldVersion) return;

    // 破棄するとマージ対象そのものが消える。保留フラグを残すと以降のアクセスで永久に空振りするため
    // ここで一緒に落とす。ユーザーはマージ待ちのカートを失うため記録も残す
    if (hasCartMergePending()) {
      Sentry.captureMessage('マージ保留中のゲストカートをバージョン不一致で破棄した', {
        level: 'warning',
        extra: { cartVersion },
      });
      clearCartMergePending();
    }
    clearCartInfo();
  }, [clearCartInfo]);

  useEffect(() => {
    const retryPendingMerge = async () => {
      if (retryingRef.current) return;
      if (!isLogin) return;
      if (!hasCartMergePending()) return;
      // 直読みなので hydration を待たずに真値が取れる。
      // 真値が空ならマージ対象が無いだけなのでフラグは保持する（クリアは次回ログイン時の signInAndMerge）。
      const items = readLocalCartInfo().items ?? [];
      if (items.length === 0) return;

      retryingRef.current = true;
      try {
        await mergeCartWithNotification(items, clearCartInfo);
        router.refresh();
      } catch (error) {
        // 再試行も失敗。フラグは保持し、次回ページアクセスでまた試す
        Sentry.captureException(error);
      } finally {
        retryingRef.current = false;
      }
    };
    retryPendingMerge();
  }, [isLogin, clearCartInfo, router, pathname]);

  return null;
}

export default GfCheckCart;
